/**
 * The phone's Month and List read the calendar a month at a time — one
 * `GET /activity/calendar` per calendar month (the route answers up to six
 * weeks, §6.2), so the same months serve both views and switching between
 * them asks for nothing twice.
 *
 * Kept for the page's life: a month already read is drawn at once, and asked
 * again in the background the first time a view shows it, so what was moved
 * in Day is right in Month. A New sheet that made something, or a ticked
 * task, asks every month shown again.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { monthSpan, type CalendarAnswer } from '@rowted/shared'
import { activityRequest, peopleQuery } from '../api.js'
import { ACTIVITY_MADE_EVENT } from '../new/new-sheets.js'
import { TASKS_CHANGED_EVENT } from '../tasks/task-sheet.js'

interface Kept { answer: CalendarAnswer; seq: number }

/** Months read, by `<people>|<month>`; the oldest go past 48. */
const KEPT = new Map<string, Kept>()
const KEEP = 48
/** Every answer gets the next number, so a view can tell what was read before it opened. */
let seq = 0

function keep(key: string, answer: CalendarAnswer): void {
  KEPT.delete(key)
  KEPT.set(key, { answer, seq: ++seq })
  while (KEPT.size > KEEP) KEPT.delete(KEPT.keys().next().value!)
}

/** Forget every month read — between tests. */
export function forgetMonths(): void {
  KEPT.clear()
}

/**
 * `months` are asked for (the ones on screen and either side); `shown` — every
 * month drawn, by default the same — are answered from what is kept, so a day
 * scrolled away from keeps its things and its height rather than going back to
 * *Loading…* above somebody's thumb.
 */
export function useCalendarMonths(people: readonly string[], months: readonly string[], shown: readonly string[] = months) {
  const who = peopleQuery(people)
  /* Anything read at or before this was read before the view opened (or before a reload): ask again. */
  const since = useRef(seq)
  const asking = useRef(new Set<string>())
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [tick, setTick] = useState(0)
  const wanted = months.join(',')
  /* An answer that lands after the view has gone is kept, and draws nothing. */
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  useEffect(() => {
    if (!who) return
    for (const month of wanted.split(',').filter(Boolean)) {
      const key = `${who}|${month}`
      const kept = KEPT.get(key)
      if ((kept && kept.seq > since.current) || asking.current.has(key)) continue
      asking.current.add(key)
      const { from, to } = monthSpan(month)
      activityRequest<CalendarAnswer>(`/activity/calendar?people=${who}&from=${from}&to=${to}`)
        .then((answer) => {
          keep(key, answer)
          if (!mounted.current) return
          setErrors((was) => {
            if (!(month in was)) return was
            const rest = { ...was }
            delete rest[month]
            return rest
          })
          setTick((n) => n + 1)
        })
        .catch((cause: unknown) => {
          if (mounted.current) setErrors((was) => ({ ...was, [month]: cause instanceof Error ? cause.message : String(cause) }))
        })
        .finally(() => asking.current.delete(key))
    }
  }, [who, wanted, tick])

  const reload = useCallback(() => {
    since.current = seq
    setTick((n) => n + 1)
  }, [])
  useEffect(() => {
    window.addEventListener(ACTIVITY_MADE_EVENT, reload)
    window.addEventListener(TASKS_CHANGED_EVENT, reload)
    return () => {
      window.removeEventListener(ACTIVITY_MADE_EVENT, reload)
      window.removeEventListener(TASKS_CHANGED_EVENT, reload)
    }
  }, [reload])

  /* A new object whenever an answer lands, so what reads it draws again. */
  const drawn = shown.join(',')
  const answers = useMemo(() => {
    const out = new Map<string, CalendarAnswer>()
    for (const month of new Set([...wanted.split(','), ...drawn.split(',')])) {
      const kept = month ? KEPT.get(`${who}|${month}`) : undefined
      if (kept) out.set(month, kept.answer)
    }
    return out
  }, [who, wanted, drawn, tick])

  return { answers, errors, reload }
}
